import { defineStore } from 'pinia'
import { onScopeDispose, ref } from 'vue'

import { sampleSize } from 'lodash'

import api from '@app/insights/api'
import I18n from '@app/adapters/I18n'
import { SfLog } from '@app/adapters/logger'

import { createBusSubscription } from './bus-subscription'

// A suggestion the assistant offers before a conversation starts: the pill carries the
// tagline, clicking it submits the full prompt.
export interface SuggestedPrompt {
  tagline: string
  prompt: string
}

interface SuggestedPromptsResponse {
  prompts: SuggestedPrompt[]
  // Whether a warm job is generating a batch that will arrive as a MessageBus push. False
  // with empty prompts means the backend's failure cooldown: nothing is coming.
  warming: boolean
  message_bus_channel: string
  message_bus_last_id: number
}

// Published by Insights::AgentSuggestedPromptsJob once a batch is generated.
type SuggestedPromptsPayload = {
  prompts: SuggestedPrompt[]
  locale: string
}

export const SUGGESTIONS_SHOWN = 3

// How long a caller waits for the warm job to publish a batch before it gives up and
// renders no suggestions at all. Generation is a single Flash call; a batch that has not
// arrived by now is not coming (provider error, or the job died with retry: 0).
export const SUGGESTIONS_PUSH_TIMEOUT_MS = 20_000

// Firm-tailored example prompts, shared by the assistant drawer's empty state and the
// Ask AI card on the queries overview.
export const useSuggestedPromptsStore = defineStore(
  'insights-suggested-prompts',
  () => {
    // The prompts on screen, sampled from the pool.
    const suggestions = ref<SuggestedPrompt[]>([])
    const pool = ref<SuggestedPrompt[]>([])
    // Until the first load resolves, callers show a skeleton rather than an empty list.
    const loaded = ref<boolean>(false)

    const subscription = createBusSubscription(handlePushedBatch)
    let pushTimeout: number | null = null

    // Call on each mount of a surface that shows the pills. It advances the backend's show
    // counter, and re-samples an existing pool so the pills render without waiting on the
    // network. On a cold cache the response carries no prompts and the batch arrives later
    // over MessageBus: there is no static fallback, because a suggestion the firm's data
    // cannot answer is worse than no suggestion.
    async function load(): Promise<void> {
      const hadPrompts = pool.value.length > 0
      if (hadPrompts) resample()

      try {
        const response =
          (await api.fetchAgentSuggestedPrompts()) as SuggestedPromptsResponse

        subscription.subscribe(
          response.message_bus_channel,
          response.message_bus_last_id ?? 0
        )

        const prompts = response.prompts ?? []
        if (prompts.length) {
          apply(prompts, { resample: !hadPrompts })
        } else if (!hadPrompts) {
          if (response.warming) {
            // A warm job is generating: wait for its push, but not forever.
            awaitPush()
          } else {
            // No warm in flight means the backend's failure cooldown: no push is coming.
            loaded.value = true
          }
        }
      } catch (error) {
        SfLog.error('Insights assistant suggested prompts failed', { error })
        loaded.value = true
      }
    }

    // Resampling is skipped when the view already shows pills, so a background refresh
    // never reshuffles them mid-view.
    function apply(
      prompts: SuggestedPrompt[],
      { resample: shouldResample }: { resample: boolean }
    ): void {
      pool.value = prompts.map(withTagline)
      loaded.value = true
      if (shouldResample) resample()
    }

    // A batch of bare sentences can still sit in the MessageBus backlog. Each sentence
    // doubles as its own pill label.
    function withTagline(
      suggestion: SuggestedPrompt | string
    ): SuggestedPrompt {
      if (typeof suggestion === 'string')
        return { tagline: suggestion, prompt: suggestion }

      return suggestion
    }

    function awaitPush(): void {
      clearPushTimeout()
      pushTimeout = window.setTimeout(() => {
        loaded.value = true
      }, SUGGESTIONS_PUSH_TIMEOUT_MS)
    }

    function clearPushTimeout(): void {
      if (pushTimeout === null) return

      window.clearTimeout(pushTimeout)
      pushTimeout = null
    }

    // A batch generated for a locale the user has since left would render in the wrong
    // language, so it is dropped. Matched on base language, because the prompts vary only by
    // language and the server's locale ("nl") and the client's ("nl-BE") differ by region.
    function handlePushedBatch(payload: SuggestedPromptsPayload): void {
      if (!payload?.prompts?.length) return
      if (
        payload.locale &&
        baseLanguage(payload.locale) !== baseLanguage(I18n.locale)
      )
        return

      clearPushTimeout()
      apply(payload.prompts, { resample: !suggestions.value.length })
    }

    function baseLanguage(locale: string): string {
      return (locale || '').slice(0, 2)
    }

    function resample(): void {
      suggestions.value = sampleSize(pool.value, SUGGESTIONS_SHOWN)
    }

    onScopeDispose(() => {
      clearPushTimeout()
      subscription.dispose()
    })

    return { suggestions, loaded, load }
  }
)
